import {useEffect} from 'react';
import {appConfig, gaConfig} from '@stencil/hotels-config';

import {useSelector, useDispatch} from 'react-redux';

//Selectors
import {
  searchHotelDetailsSelector,
  contextDetailsSelector,
  hotelLocationSelector,
  hotelNameSelector,
  luckyDataSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

import * as S from './PreviousBooking.styles';
import {BaseColumnWrap} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {defaultTheme} from '@stencil/build-theme';
import HappyTickMarkIcon from '@stencil/icons/src/HappyTickMarkIcon/HappyTickMarkIcon';

import FreeCancelIcon from '../../../../../stories/Icons/FreeCancelIcon';
import NonRefundableIcon from '../../../../../stories/Icons/NonRefundable';
import RoomInfoText from '../RoomSelection/RoomInfoText';
import PreviousBookingBookNowButton from '@stencil/book-now-previous-booking';
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {createPaxStringFromRoomTariffs} from '@stencil/hotels-utils/src/common-utils';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import {getSlashAndShowPriceCg} from '@stencil/hotels-utils/src/get-details-slash-and-show-price';

//Types
import {RoomBase} from '@stencil/hotels-cg-reducer/src/details/types';

const {rupeeString, multiRoomDefaultNoImagePlacholder} = appConfig;

interface DetailQuickBookCardProps {
  data: RoomBase[];
}

export default function DetailQuickBookCard({data = []}) {
  const hotelDetails = useSelector(searchHotelDetailsSelector);
  const contextDetails = useSelector(contextDetailsSelector);
  const hotelName = useSelector(hotelNameSelector);
  const hotelLocation = useSelector(hotelLocationSelector);
  const luckyData = useSelector(luckyDataSelector);
  const hotelArea = hotelLocation?.name;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      detailsPagePushGaThunk({
        event: gaConfig.detailsPageClickEventCategoryName,
        eventCategory: gaConfig.detailsPageClickEventCategoryName,
        eventAction: gaConfig.DetailPageGAEvents.detailsQuickBook.quickbook_HD_shown + '_2',
      }),
    );
  }, []);

  const onBookNowClick = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.QuickBookCard}_${detailsClick.detailsQuickBook.quickbook_HD_click}_2`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.QuickBookCard,
          destination: '',
        },
      }),
    );
  };

  return (
    <>
      {data.map((item, index) => {
        const roomCode = item?.roomCode;
        const roomName = item?.roomName;
        const roomHighlights = item?.roomHighlights;
        const roomImage = item?.images?.[0];

        const roomFacilities =
          item?.amenities?.[0].facilities
            .filter((item, index) => index < 2)
            .map(item => item?.name) || [];
        return item.ratePlans.map(roomPlanItem => {
          if (roomPlanItem) {
            const refundStatus =
              roomPlanItem?.cancellationPolicy?.type === 'FC' ||
              roomPlanItem?.cancellationPolicy?.type === 'FCC';
            const refundStatusText = roomPlanItem?.cancellationPolicy?.text;
            const refundStatusTextColor = refundStatus ? 'green' : 'red';
            const tariffs = roomPlanItem?.tariffs?.[0] || {};
            const pax = createPaxStringFromRoomTariffs(tariffs?.roomTariffs);
            const {slashedPrice, showPrice, tnc} = getSlashAndShowPriceCg(tariffs);
            const updatedRoomPlanItem = {
              fwdp: tariffs?.hermesData?.[0]?.paxWiseInfo?.[0]?.forwardParams,
              rpc: roomPlanItem?.rpc,
              offercode: tariffs?.hermesData?.[0]?.offerCode,
              ibp: hotelDetails?.supplierId,
              pm: roomPlanItem?.payMode,
              rtc: roomCode,
              gseg: tariffs?.hermesData?.[0]?.segmentId,
              cot: tariffs?.hermesData?.[0]?.contractType,
              hmd: tariffs?.hermesData?.[0]?.paxWiseInfo?.[0]?.hermesMetaData,
              gsv: roomPlanItem?.supplierCode,
              pc: tariffs?.hermesData?.[0]?.promoCode,
              cur: contextDetails?.currency,
              pd: {
                pax,
                spr: showPrice,
              },
              isLucky: roomPlanItem?.isLucky,
            };
            return (
              <>
                <S.DQuickCardWrap>
                  <S.PreviousStayHeader>{'Book your previous stay here'}</S.PreviousStayHeader>
                  <S.DBookCard>
                    <S.QbRoomDetailWrap>
                      <S.RoomImage src={roomImage || multiRoomDefaultNoImagePlacholder} />
                      <BaseColumnWrap>
                        <S.RoomDetails>{roomName}</S.RoomDetails>
                        <S.RoomDescriptionWrapper>
                          {roomHighlights?.map(item => {
                            return (
                              <li>
                                <span>
                                  <img
                                    src={item?.iconUrl}
                                    height="14"
                                    width="14"
                                    style={{
                                      marginRight: '0.8rem',
                                    }}
                                  />
                                </span>
                                <span>{item?.text}</span>
                              </li>
                            );
                          })}
                        </S.RoomDescriptionWrapper>
                      </BaseColumnWrap>
                    </S.QbRoomDetailWrap>
                    <S.QbAmenitiesDetailWrap>
                      <S.RoomPlanTag>{'Room Plan Includes'}</S.RoomPlanTag>
                      <S.RoomDescriptionWrapper>
                        {roomFacilities && roomFacilities?.length > 0 ? (
                          <li>
                            <HappyTickMarkIcon
                              height="1rem"
                              width="1rem"
                              margin="0 0.8rem 0 0"
                              fill={defaultTheme.colors.downriver.base}
                            />
                            <span>{roomFacilities.join(', ')}</span>
                          </li>
                        ) : null}

                        <li>
                          {refundStatusText && (
                            <RoomInfoText textColor={refundStatusTextColor} alignItems="center">
                              <S.RoomOptionsIconContainer>
                                {!!refundStatus ? (
                                  <FreeCancelIcon
                                    width="1.3rem"
                                    height="1.3rem"
                                    fill={refundStatusTextColor}
                                  />
                                ) : (
                                  <NonRefundableIcon
                                    width="1.3rem"
                                    height="1.3rem"
                                    fill={refundStatusTextColor}
                                    transform="scale(1.3)"
                                  />
                                )}
                              </S.RoomOptionsIconContainer>

                              <S.CancellationPolicyContainer>
                                <span>{refundStatusText}</span>
                              </S.CancellationPolicyContainer>
                            </RoomInfoText>
                          )}
                        </li>
                      </S.RoomDescriptionWrapper>
                    </S.QbAmenitiesDetailWrap>
                    <S.QbPricingDetailWrap>
                      <BaseColumnWrap>
                        <S.StrickedPrice>
                          {rupeeString}
                          {slashedPrice}
                        </S.StrickedPrice>
                        <S.OriginalPricePrice>
                          {rupeeString}
                          {showPrice}
                        </S.OriginalPricePrice>
                        <S.TaxFeePara>
                          + {rupeeString} {tnc} taxes & fees
                        </S.TaxFeePara>
                      </BaseColumnWrap>
                      {roomPlanItem && (
                        <PreviousBookingBookNowButton
                          // roomPlanData={roomPlanItem}
                          roomPlanData={updatedRoomPlanItem}
                          roomTypeName={roomName}
                          hotelData={{
                            area: hotelArea,
                            name: hotelName,
                          }}
                          cityid={hotelDetails?.voyagerCityId}
                          onBookNowClick={() => onBookNowClick()}
                          luckyData={luckyData}
                        />
                      )}
                    </S.QbPricingDetailWrap>
                  </S.DBookCard>
                </S.DQuickCardWrap>
              </>
            );
          }
        });
      })}
    </>
  );
}
